// VerifySessionPopover — 세션 DB 측 ground truth (V$SESSION) 조회 결과를 보여주는 mini popover.
//
// SchemaExplorer 의 세션 노드 verify 아이콘 클릭 시 anchorRect 기반으로 위치 결정.
// 외부 클릭 / ESC 로 닫힘.
//
// 표시 케이스:
//   loading            → spinner + "확인 중..."
//   error (network)    → 에러 메시지
//   alive=true         → V$SESSION 행 표 (status, state, event, wait_class, sql_id, ...)
//   alive=true + fallback='mystat' → "V$SESSION 권한 없음 — alive 만 확인됨"
//   alive=false        → "세션이 V$SESSION 에 없음 (이미 종료됨)"
//   supported=false    → "이 DB 는 verify 미지원 (Oracle 만)"

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FiX, FiAlertCircle, FiCheckCircle, FiHelpCircle } from 'react-icons/fi';
import styles from './VerifySessionPopover.module.css';

import { toCssPx } from '../utils/appZoom';
const POPOVER_WIDTH = 360;
const POPOVER_OFFSET_Y = 6;

function pickPosition(anchorRect) {
    if (!anchorRect) return { top: 80, left: 80 };
    // 아래 계산은 화면 px(rect·innerWidth) — 돌려줄 때 CSS px 로(웹의 화면 확대, utils/appZoom)
    const margin = 8;
    let left = anchorRect.left;
    // 우측 화면 밖으로 안 나가도록
    if (left + POPOVER_WIDTH > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - POPOVER_WIDTH - margin);
    }
    let top = anchorRect.bottom + POPOVER_OFFSET_Y;
    // 하단 잘림 방지 — 위로 띄움
    if (top + 200 > window.innerHeight - margin) {
        top = Math.max(margin, anchorRect.top - 200 - POPOVER_OFFSET_Y);
    }
    return { top: toCssPx(top), left: toCssPx(left) };
}

const Row = ({ label, value, hint }) => (
    <div className={styles.row}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value} title={hint || (value != null ? String(value) : '')}>
            {value != null && value !== '' ? value : <span className={styles.dim}>—</span>}
        </span>
    </div>
);

Row.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hint: PropTypes.string,
};

const VerifySessionPopover = ({ state, onClose }) => {
    const { t } = useTranslation();
    const ref = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const { loading, result, error, anchorRect } = state || {};
    const pos = pickPosition(anchorRect);

    let body;
    if (loading) {
        body = (
            <div className={styles.center}>
                <span className={styles.spinner} />
                <span>{t('common:vs.checking')}</span>
            </div>
        );
    } else if (error) {
        body = (
            <div className={styles.errorBox}>
                <FiAlertCircle />
                <div>
                    <div className={styles.errorTitle}>{t('common:vs.failed')}</div>
                    <div className={styles.errorMsg}>{error}</div>
                </div>
            </div>
        );
    } else if (result && result.supported === false) {
        body = (
            <div className={styles.infoBox}>
                <FiHelpCircle />
                <div>
                    <div className={styles.infoTitle}>{t('common:vs.unsupported')}</div>
                    <div className={styles.infoMsg}>{result.reason || t('common:vs.oracleOnly')}</div>
                </div>
            </div>
        );
    } else if (result && result.alive === false) {
        body = (
            <div className={styles.errorBox}>
                <FiAlertCircle />
                <div>
                    <div className={styles.errorTitle}>{t('common:vs.noSession')}</div>
                    <div className={styles.errorMsg}>
                        {result.reason || result.error || t('common:vs.notInVSession')}
                    </div>
                </div>
            </div>
        );
    } else if (result && result.fallback === 'mystat') {
        body = (
            <div className={styles.infoBox}>
                <FiCheckCircle style={{ color: '#10b981' }} />
                <div>
                    <div className={styles.infoTitle}>connection alive</div>
                    <div className={styles.infoMsg}>
                        {t('common:vs.noPrivilege')}
                    </div>
                </div>
            </div>
        );
    } else if (result && result.alive) {
        body = (
            <div className={styles.detail}>
                <div className={styles.headerOk}>
                    <FiCheckCircle /> {t('common:vs.aliveOnDb')}
                </div>
                <Row label="SID · SERIAL#" value={`${result.dbSid}${result.dbSerial != null ? ' · ' + result.dbSerial : ''}`} />
                <Row label="status" value={result.status} hint={t('common:vs.stateHint')} />
                <Row label="state" value={result.state} />
                <Row label="event" value={result.event} hint={t('common:vs.waitHint')} />
                <Row label="wait class" value={result.waitClass} />
                <Row label="sql_id" value={result.sqlId} />
                <Row label="last_call_et" value={result.lastCallEt != null ? `${result.lastCallEt}s` : null} hint={t('common:vs.sinceLastCall')} />
                <Row label="seconds_in_wait" value={result.secondsInWait != null ? `${result.secondsInWait}s` : null} />
            </div>
        );
    } else {
        body = <div className={styles.center}>{t('common:vs.noResult')}</div>;
    }

    return (
        <div
            ref={ref}
            className={styles.popover}
            style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
            role="dialog"
            aria-label={t('common:vs.title')}
        >
            <div className={styles.titleBar}>
                <span className={styles.title}>{t('common:vs.title')}</span>
                <button type="button" className={styles.closeBtn} onClick={onClose} title={t('common:vs.close')}>
                    <FiX size={14} />
                </button>
            </div>
            <div className={styles.body}>{body}</div>
        </div>
    );
};

VerifySessionPopover.propTypes = {
    state: PropTypes.shape({
        sessionId: PropTypes.string,
        anchorRect: PropTypes.object,
        loading: PropTypes.bool,
        result: PropTypes.object,
        error: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

export default VerifySessionPopover;
